import type { ButtonComponentProps } from '@timelyapp/react';
import { Button, LinkButton } from '@timelyapp/react';
import React, { useCallback, useMemo } from 'react';
import { useHref } from 'react-router-dom';
import type { TextContent, UpgradePermissionsInfo } from 'redux/modules/subscriptions.types';
import { TrackingActions } from 'tracking/actions';

interface Props extends ButtonComponentProps {
  className?: string;
  upgradePermissionsInfo: UpgradePermissionsInfo;
  upgradeText?: TextContent;
  hideGotIt?: boolean;
  featureTrackingKey: string;

  onContactSupport: () => void;
  onContactWorkspaceOwner: () => void;
  onDismiss?: () => void;
}

const AdaptiveUpgradeButton: React.FC<Props> = props => {
  const {
    onContactSupport,
    onContactWorkspaceOwner,
    onDismiss,
    className,
    upgradePermissionsInfo,
    hideGotIt,
    featureTrackingKey,
    upgradeText,
    ...buttonProps
  } = props;
  const {
    canUserUpgradeSubscription,
    shouldOpenPlansModal,
    shouldOpenExternalSalesPage,
    canContactWorkspaceOwner,
    canContactAccountManager,
  } = upgradePermissionsInfo;

  const handleContactSupport = useCallback(() => {
    onContactSupport?.();
    TrackingActions.upgradeOpportunity('Contact account manager', featureTrackingKey);
  }, [featureTrackingKey, onContactSupport]);

  const handleGotIt = useCallback(() => {
    onDismiss?.();
    TrackingActions.upgradeOpportunity('Got it', featureTrackingKey);
  }, [featureTrackingKey, onDismiss]);

  const handleUpgradeClick = useCallback(() => {
    TrackingActions.upgradeOpportunity('Upgrade my plan', featureTrackingKey);
  }, [featureTrackingKey]);

  const handleContactWorkspaceOwner = useCallback(() => {
    onContactWorkspaceOwner?.();
    TrackingActions.upgradeOpportunity('Contact workspace admin', featureTrackingKey);
  }, [featureTrackingKey, onContactWorkspaceOwner]);

  const upgradeUrl = useHref(
    `/settings/subscription${shouldOpenPlansModal ? '?open_modal=plans' : ''}`
  );

  const upgradeLink = useMemo(() => {
    if (shouldOpenExternalSalesPage) {
      return 'https://timelyapp.com/sales';
    } else {
      return upgradeUrl;
    }
  }, [upgradeUrl, shouldOpenExternalSalesPage]);

  return canUserUpgradeSubscription ? (
    <LinkButton
      priority="xhigh"
      size="large"
      onClick={handleUpgradeClick}
      linkTo={upgradeLink}
      newWindow={shouldOpenExternalSalesPage}
      className={className}
      data-testid="upgrade-link"
      {...buttonProps}
    >
      <span>{(typeof upgradeText !== 'function' && upgradeText) || 'Upgrade my plan'}</span>
    </LinkButton>
  ) : canContactWorkspaceOwner ? (
    <Button
      onClick={handleContactWorkspaceOwner}
      priority="xhigh"
      size="large"
      className={className}
      data-testid="contact-workspace-admin-button"
      {...buttonProps}
    >
      Contact workspace admin
    </Button>
  ) : canContactAccountManager ? (
    <Button
      onClick={handleContactSupport}
      priority="xhigh"
      size="large"
      className={className}
      data-testid="contact-account-manager-button"
      {...buttonProps}
    >
      Contact account manager
    </Button>
  ) : !hideGotIt ? (
    <Button
      onClick={handleGotIt}
      priority="xhigh"
      size="large"
      className={className}
      data-testid="contact-workspace-got-it"
      {...buttonProps}
    >
      Got it
    </Button>
  ) : null;
};

export default AdaptiveUpgradeButton;
